import { Component, EventEmitter, Input, OnInit, AfterViewChecked, OnDestroy, Output, ViewChild } from '@angular/core';

@Component({
    selector: 'html-editor',
    templateUrl: './html-editor.component.html',
    standalone: false
})
export class HtmlEditorComponent implements OnInit, AfterViewChecked, OnDestroy {
  @Input() currentBody;
  @Input() hideToolbar?: boolean;
  @Input() listenToChangeEvents?: boolean;

  @Output() reqSave = new EventEmitter();
  @Output() htmlEditorContentsChanged = new EventEmitter();

  @ViewChild('myckeditor', { static: false }) public myckeditor: any;

  editorReady: boolean = false;
  changeTimeout: any;

  ckEditorConfig: {} = {
    height: 100,
    startupFocus: true,
    scayt_autoStartup: false,
    disableNativeSpellChecker: false,
    contentsCss: ["body {font-size: 18px; font-family: 'Source Sans Pro', sans-serif !important;} img {max-width: 100%;}"],
    toolbar: [
      { name: 'document', groups: ['mode', 'document', 'doctools'], items: ['Source', '-', 'ExportPdf', 'Preview', '-', 'Templates'] },
      { name: 'clipboard', groups: ['clipboard', 'undo'], items: ['Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord', '-', 'Undo', 'Redo'] },
      { name: 'editing', groups: ['find', 'selection'], items: ['Find', 'Replace', '-', 'SelectAll'] },
      { name: 'forms', items: ['Form', 'Checkbox', 'Radio', 'TextField', 'Textarea', 'Select', 'Button', 'ImageButton', 'HiddenField'] },
      '/',
      { name: 'basicstyles', groups: ['basicstyles', 'cleanup'], items: ['Bold', 'Italic', 'Underline', 'Strike', 'Subscript', 'Superscript', '-', 'CopyFormatting', 'RemoveFormat'] },
      { name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align', 'bidi'], items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', '-', 'Blockquote', 'CreateDiv', '-', 'JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock', '-', 'BidiLtr', 'BidiRtl', 'Language'] },
      { name: 'links', items: ['Link', 'Unlink', 'Anchor'] },
      { name: 'insert', items: ['Image', 'Table', 'HorizontalRule', 'Smiley', 'SpecialChar', 'PageBreak'] },
      '/',
      { name: 'styles', items: ['Styles', 'Format', 'Font', 'FontSize'] },
      { name: 'colors', items: ['TextColor', 'BGColor'] },
      { name: 'tools', items: ['Maximize', 'ShowBlocks'] },
      { name: 'others', items: ['-'] }
    ],
    toolbarGroups: [
      { name: 'document', groups: ['mode', 'document', 'doctools'] },
      { name: 'clipboard', groups: ['clipboard', 'undo'] },
      { name: 'editing', groups: ['find', 'selection'] },
      { name: 'forms' },
      '/',
      { name: 'basicstyles', groups: ['basicstyles', 'cleanup'] },
      { name: 'paragraph', groups: ['list', 'indent', 'blocks', 'align', 'bidi'] },
      { name: 'links' },
      { name: 'insert' },
      '/',
      { name: 'styles' },
      { name: 'colors' },
      { name: 'tools' },
      { name: 'others' },
      { name: 'about' }
    ],
    // removeButtons: 'Save,NewPage,Print,Templates,Cut,Copy,Paste,PasteText,PasteFromWord,Replace,Font,FontSize,SelectAll,Styles,Form,Checkbox,Radio,Select,Button,ImageButton,HiddenField,Strike,CopyFormatting,CreateDiv,BidiLtr,BidiRtl,Language,Anchor,Image,Flash,Table,HorizontalRule,Smiley,SpecialChar,PageBreak,Iframe,Maximize,ShowBlocks,About,Scayt'
    removeButtons: 'Scayt',
    removePlugins: 'wsc,scayt',
    // Enable Upload tab in the Image dialog + drag/drop image upload. Points at a
    // dedicated route so the shared /api/files/upload endpoint stays unchanged for
    // CKEditor 5 and every other consumer. The ?src= query params keep the URL well-formed
    // when CKEditor appends its own &responseType=json.
    filebrowserImageUploadUrl: '/api/files/upload-ckeditor4?src=filebrowser',
    uploadUrl: '/api/files/upload-ckeditor4?src=uploadwidget',
    // Adds a "Center Image" option to the right-click menu on images. CKEditor 4's built-in
    // Image dialog only supports left/right float; this centers the image by lifting it into
    // its own centered paragraph so surrounding text stays put in paragraphs before/after.
    on: {
      instanceReady: (evt: any) => {
        const editor = evt.editor;
        if (!editor) return;

        editor.addCommand('centerImage', {
          exec: (ed: any) => {
            const selection = ed.getSelection();
            if (!selection) return;
            const img = selection.getSelectedElement();
            if (!img || img.getName() !== 'img') return;

            // Strip float / align the Image dialog may have applied - they fight text-align.
            img.removeStyle('float');
            img.removeAttribute('align');

            // New centered paragraph containing a clone of the image. insertElement replaces
            // the selected image and lets CKEditor split the containing block around it.
            const wrapper = ed.document.createElement('p');
            wrapper.setStyle('text-align', 'center');
            wrapper.append(img.clone(true, true));

            ed.insertElement(wrapper);
            ed.fire('change');
          }
        });

        editor.addMenuGroup('imageAlign');
        editor.addMenuItem('centerImage', {
          label: 'Center Image',
          command: 'centerImage',
          group: 'imageAlign'
        });

        if (editor.contextMenu) {
          editor.contextMenu.addListener((element: any) => {
            if (element && element.getName && element.getName() === 'img') {
              return { centerImage: 2 }; // 2 = CKEDITOR.TRISTATE_OFF (enabled, not-active). Do not use 0 which is DISABLED in this build.
            }
          });
        }
      }
    }
  };

  constructor() { }

  ngOnInit(): void {

  }


  ngAfterViewChecked() {
    // Will keep checking until instance is true.
    if (!this.editorReady && this.myckeditor?.instance) {
      this.editorReady = true; // Stop outer condition from checking after it reaches this.

      // Only do this if listenToChangeEvents is passed true so it doesn't break things that used before adding.
      if (this.listenToChangeEvents) {
        const editorInstance = this.myckeditor.instance;
        // console.log('editorInstance: ', editorInstance);

        editorInstance.on('key', () => {
          // Debounce using setTimeout to wait and see when they stop typing.
          clearTimeout(this.changeTimeout);
          this.changeTimeout = setTimeout(() => {
            this.onEditorChange();
          }, 2000); // Fires after typing stops for this amount of time.
        });
      }
    }
  }


  ngOnDestroy(): void {
    if (this.changeTimeout) {
      clearTimeout(this.changeTimeout);
      this.changeTimeout = null;
    }

    if (this.myckeditor != undefined) this.myckeditor = null;
    this.editorReady = false;
  }


  onEditorChange() {
    // Access the CKEditor instance
    if (this.myckeditor != undefined) {
      const editorInstance = this.myckeditor.instance;

      // Access the data from the CKEditor instance
      const editorData = editorInstance.getData();
      // console.log('Editor data:', editorData);

      if (this.currentBody !== editorData) this.currentBody = editorData;
    }

    // console.log('Editor changed to current body: ', this.currentBody);
    this.htmlEditorContentsChanged.next(this.currentBody);
  }


  public getHtml() {
    return this.currentBody;
  }


  public setCursorToEnd() {
    const editor = this.myckeditor?.instance ?? null;
    if (!editor || !editor.editable()) return;

    editor.focus();

    const range = editor.createRange();
    // console.log('Range: ', range);
    range.moveToElementEditEnd(editor.editable());
    editor.getSelection().selectRanges([range]);
  }


  public addAtCursor(_string) {
    const editor = this.myckeditor?.instance;
    if (!editor) return;

    // CKEditor's insertText crashes with "Cannot read properties of null (reading 'hasAscendant')" when there's no active selection (user clicked Insert before clicking in the editor). Ensure a valid cursor exists first.
    const _selection = editor.getSelection();
    if (!_selection || !_selection.getStartElement()) this.setCursorToEnd();

    // CKEditor's insertText() drops bare text outside any inline-style spans
    // surrounding the caret, so merge fields render with a different font in
    // Gmail than the rest of the email. Mirror the caret's inline styles onto
    // the inserted text so it inherits the same look.
    const styleProps = ['font-family', 'font-size', 'color', 'background-color', 'font-weight', 'font-style', 'text-decoration', 'line-height'];
    const styles: { [key: string]: string } = {};
    const editable = editor.editable();
    let node: any = editor.getSelection()?.getStartElement();
    while (node && typeof node.getStyle === 'function' && !node.equals(editable)) {
      for (const prop of styleProps) {
        if (!styles[prop]) {
          const val = node.getStyle(prop);
          if (val) styles[prop] = val;
        }
      }
      if (typeof node.getName === 'function' && node.getName() === 'font' && !styles['font-family']) {
        const face = node.getAttribute('face');
        if (face) styles['font-family'] = face;
      }
      node = node.getParent();
    }

    const styleStr = Object.keys(styles).map(k => `${k}: ${styles[k]}`).join('; ');
    if (styleStr) {
      const escaped = _string.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      editor.insertHtml(`<span style="${styleStr}">${escaped}</span>`);
    } else {
      editor.insertText(_string);
    }
  }


  public addHtmlAtCursor(_string) {
    this.myckeditor.instance.insertHtml(_string);
  }


  public addAtCursorWithContentCheck(_string: string) {
    if (!this.myckeditor?.instance) return;

    const content = (_string || '').trim();
    if (!content.length) return;

    // Quick test: looks like HTML if it has any tag pairs or single tags.
    const looksLikeHtml = /<\/?[a-z][\s\S]*>/i.test(content);

    try {
      if (looksLikeHtml) {
        this.myckeditor.instance.insertHtml(content); // Try inserting as HTML first.
      } else {
        this.myckeditor.instance.insertText(content); // Treat as plain text.
      }
    } catch (err) {
      console.log('addAtCursorWithContentCheck(): Failed to insert as HTML, falling back to text.', err);
      this.myckeditor.instance.insertText(content); // Fallback: treat as plain text,
    }
  }
}